import { linkUpdateSource, packageUpdateSource } from '../data/busSources.js';
import type { NotifyLinkChangeInput } from '../types/NotifyLinkChangeInput.js';
import type { PartialContext } from '../types/Context.js';
import { notifyReload, type NotifyReloadOptions } from './notifyReload.js';
import type { ResolveMapEntry } from '@ms-cloudpack/common-types';
import { createPackageUpdatePayload } from './createPackageUpdatePayload.js';

type NotifyHostOptions = Omit<NotifyLinkChangeInput, 'packagePath'> &
  NotifyReloadOptions & {
    packageEntry: Pick<ResolveMapEntry, 'path' | 'name' | 'version'>;
  };

/**
 * Notifies clients that a reload is needed and notifies the host of link changes.
 *
 * If HMR is enabled and the package has entry points in the import map, publishes an HMR update
 * with all entry points for the browser to handle. The browser-side code determines whether React
 * components were updated and either performs fast refresh or falls back to full page reload.
 *
 * Always publishes link updates and reload notifications for backward compatibility and telemetry.
 */
export async function notifyHost(
  options: NotifyHostOptions,
  context: PartialContext<
    'bus' | 'telemetryClient' | 'session',
    'sequence' | 'importMap' | 'packageImportPaths' | 'config'
  >,
): Promise<void> {
  const { reason, packageEntry, hash, ...input } = options;
  const { bus, session } = context;

  // Always publish link updates
  bus.publish(linkUpdateSource, { packagePath: packageEntry.path, hash, ...input });

  // HMR: Publish package update with all entry points (if HMR is enabled)
  const packageUpdatePayload =
    session.config.features?.hmr && hash
      ? createPackageUpdatePayload({ input: { packageEntry, hash }, context })
      : undefined;
  if (packageUpdatePayload) {
    bus.publish(packageUpdateSource.path, packageUpdatePayload);
    return;
  }

  await notifyReload({ reason }, context);
}
